const { DateTime, Duration, Interval } = require('luxon')

// returns month and date, i.e. `February 11`
export const toFullDate = startDateTime => {
  const dt = DateTime.fromISO(startDateTime)

  if (!dt.isValid) return null

  return `${dt.toFormat('LLLL')} ${dt.toFormat('d')}`
}

export const toFullDateWithTimezone = (startDateTime, timezone) => {
  const dt = DateTime.fromISO(startDateTime).setZone(timezone)

  if (!dt.isValid) return null

  return `${dt.toFormat('LLLL')} ${dt.toFormat('d')}`
}

// returns month and date, i.e. `Feb 11`
export const toFullDateShortMonth = startDateTime => {
  const dt = DateTime.fromISO(startDateTime)

  if (!dt.isValid) return null

  return `${dt.toFormat('LLL')} ${dt.toFormat('d')}`
}

export const toFullDateAndTime = (startDateTime, timezone = null) => {
  const dt = timezone
    ? DateTime.fromISO(startDateTime).setZone(timezone)
    : DateTime.fromISO(startDateTime)
  if (!dt.isValid) return null

  return dt.toFormat('ff')
}

export const toLuxonFromIso = dateTime => {
  if (!dateTime) return null
  return DateTime.fromISO(dateTime)
}

export const isToday = date => {
  const today = DateTime.local()
  const dt = DateTime.fromISO(date)
  return today.hasSame(dt, 'day')
}

export const isTomorrow = date => {
  const tomorrow = DateTime.local().plus({ days: 1 })
  const dt = DateTime.fromISO(date)
  return tomorrow.hasSame(dt, 'day')
}

// returns time duration of a lesson, i.e. '10 - 11:30 PM'
export const getLessonDurationText = (startDateTime, timeInMinutes) => {
  const dt = DateTime.fromISO(startDateTime)

  if (!dt.isValid) return null

  const startTimeHour = `${dt.toFormat('h')}:${dt.toFormat('mm')}`
  const endTime = dt.plus({ minutes: timeInMinutes })

  return `${startTimeHour} - ${endTime.toLocaleString(DateTime.TIME_SIMPLE)}`
}

// returns full date, i.e. 'March 1, 2021'
export const toFullTextDate = startDateTime => {
  const dt = DateTime.fromISO(startDateTime)
  if (!dt.isValid) return null

  return dt.toFormat('DDD')
}

export const toFullTextDateWithTimezone = (startDateTime, timezone) => {
  const dt = DateTime.fromISO(startDateTime).setZone(timezone)
  if (!dt.isValid) return null

  return dt.toFormat('DDD')
}

// returns full date, i.e. 'Wednesday, March 1, 2021'
export const toFullTextDayDate = startDateTime => {
  const dt = DateTime.fromISO(startDateTime)
  if (!dt.isValid) return null

  return dt.toFormat('DDDD')
}

// returns time period from start and end time, i.e. '10 - 11:30pm'
export const getTimePeriod = (startDateTime, endDateTime) => {
  const start = DateTime.fromISO(startDateTime)
  const end = DateTime.fromISO(endDateTime)

  return `${start.toFormat('h')}:${start.toFormat('mm')} - ${end.toFormat('t')}`
}

export const getTimePeriodWithTimezone = (
  startDateTime,
  endDateTime,
  timezone,
) => {
  const start = DateTime.fromISO(startDateTime).setZone(timezone)
  const end = DateTime.fromISO(endDateTime).setZone(timezone)

  return `${start.toFormat('h')}:${start.toFormat('mm')} - ${end.toFormat(
    't (ZZZZ)',
  )}`
}

export const fullDayDateAndTime = (startDateTime, endDateTime) => {
  const start = DateTime.fromISO(startDateTime)
  return `${start.toFormat('cccc')} • ${start.toFormat(
    'DDD',
  )} • ${getTimePeriod(startDateTime, endDateTime)}`
}

export const fullDayDateAndTimeWithTimezone = (
  startDateTime,
  endDateTime,
  timezone,
) => {
  const start = DateTime.fromISO(startDateTime).setZone(timezone)
  return `${start.toFormat('cccc')} • ${start.toFormat(
    'DDD',
  )} • ${getTimePeriodWithTimezone(startDateTime, endDateTime, timezone)}`
}

export const daysUntil = startDateTime => {
  const start = DateTime.local()
  const end = DateTime.fromISO(startDateTime)

  const diffInDays = end.diff(start, 'days')
  const { days } = diffInDays.toObject()
  return days > 0 ? Math.floor(days) : 0
}

export const humanizeMinutes = minutes => {
  if (isNaN(minutes) || minutes === null) return ''

  if (minutes <= 60) return `${minutes} minutes`

  if (Number.isInteger(minutes / 60)) {
    return Duration.fromObject({ minutes: minutes }).toFormat("h 'hours'")
  }

  if (minutes < 120) {
    return Duration.fromObject({ minutes: minutes }).toFormat(
      "h 'hour and' mm 'minutes'",
    )
  }

  return Duration.fromObject({ minutes: minutes }).toFormat(
    "h 'hours and' mm 'minutes'",
  )
}

export const roundDateTimeMinutes = val => {
  if (!val || !val.isValid) return

  const year = val.get('year')
  const month = val.get('month')
  const day = val.get('day')
  const hour = val.get('hour')
  const minute = val.get('minute')
  return DateTime.local(year, month, day, hour, Math.floor(minute / 5) * 5)
}

export const formattedTimeWithTimezone = (
  startDateTime,
  endDateTime,
  timezone,
) => {
  const period = Interval.fromISO(`${startDateTime}/${endDateTime}`)

  return (
    <time dateTime={period.toISO()}>
      {period.hasSame('day')
        ? `${period.start
          .setZone(timezone)
          .toFormat('EEE, MMM dd \u2022 h:mm')} - ${period.end
          .setZone(timezone)
          .toFormat('t (ZZZZ)')}`
        : `${period.toFormat('EEE, MMM dd')}`}
    </time>
  )
}
